<template>
  <div v-if="!contrat.contratAgrial">
    <div
      class="d-flex flex-column mt-6 mx-4"
    >
      <AgrialInput
        v-model="libelle"
        label="Libellé *"
        hide-details
      />
    </div>
    <div
      class="d-flex flex-column mt-6 mx-4"
    >
      <AgrialSelect
        v-model="typeContrat"
        :items="typesContrat"
        item-text="libelle"
        label="Type de contrat *"
        return-object
        hide-details
      />
    </div>
    <div
      class="d-flex flex-column mt-6 mx-4"
    >
      <AgrialInputNumber
        v-model="poidsEnTonne"
        label="Poids *"
        return-object
        suffix="t"
        hide-details
      />
    </div>
    <div
      class="d-flex flex-column mt-6 mx-4"
    >
      <AgrialSelect
        v-model="typePrix"
        :items="typesPrix"
        item-text="libelle"
        label="Type de prix *"
        return-object
        hide-details
        @change="updateTypePrix"
      />
    </div>
    <div
      v-if="typePrix && typePrix.code !== 'AUCUN'"
      class="d-flex flex-column mt-6 mx-4"
    >
      <AgrialInputNumber
        v-model="prixPayeEnEuroParTonne"
        :label="typePrix.code === 'PRIX_PAYE' ? 'Prix payé *' : 'Prix contrat *'"
        suffix="€/t"
        hide-details
      />
    </div>
    <div
      v-if="typePrix && typePrix.code !== 'AUCUN'"
      class="d-flex flex-column mt-6 mx-4"
    >
      <AgrialCheckbox
        v-model="isPrixProvisoire"
        label="Prix provisoire"
        hide-details
      />
    </div>

    <BaseMessage
      v-if="erreur"
      class="mb-4 mx-5"
      color="error"
      :message="erreur"
      text
      type="error"
    />
    <div class="my-4 mt-8 ml-5 action texte-gris-fonce">
      * Champs obligatoires
    </div>
    <div class="d-flex justify-space-around">
      <AgrButton
        class="pa-5"
        outlined
        @click="onClose()"
      >
        Annuler
      </AgrButton>
      <AgrButton
        :disabled="!isValid"
        :loading="loading"
        class="pa-5"
        @click="saveContrat()"
      >
        Valider
      </AgrButton>
    </div>
  </div>
  <div v-else>
    <div class="secondary--text align-self-center intro mr-auto">
      Répartition du contrat de {{ contrat?.poidsEnTonne }} t
    </div>
    <div class="texte texte-gris-fonce">
      {{ contrat?.typeContrat?.libelle }}
    </div>
    <v-divider class="my-3" />
    <div class="mt-2 intro">
      A quelle culture voulez-vous l'affecter?
    </div>
    <v-row
      v-for="culture in contratModifiable.cultures"
      :key="culture.id"
      class="mt-6"
      no-gutters
    >
      <v-col
        cols="7"
        class="intro align-self-center primary--text"
      >
        {{ culture.typeCulture.nom }}
      </v-col>
      <v-col class="align-self-center">
        <AgrialInputNumber
          v-model="culture.poidsEnTonne"
          :rules="[rules.required]"
          label="Répartition"
          suffix="t"
          hide-details
        />
      </v-col>
    </v-row>
    <BaseMessage
      v-if="erreur"
      class="my-4 mx-5"
      color="error"
      :message="erreur"
      text
      type="error"
    />
    <BaseMessage
      v-if="hasErreurCalculRepartition"
      class="my-4 mx-5"
      color="error"
      message="Répartition des tonnages invalide"
      text
      type="error"
    />
    <div class="d-flex mt-3 justify-space-around">
      <AgrButton
        class="pa-5"
        outlined
        @click="onClose()"
      >
        Annuler
      </AgrButton>
      <AgrButton
        :disabled="!isValidRepartition"
        :loading="loading"
        class="pa-5"
        @click="saveContrat()"
      >
        Valider
      </AgrButton>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isEmptyString } from '@/utils/string'
import { sum } from 'lodash'
import { clone } from '@/utils/clone'
import { equalDecimals } from'@/utils/math'
import { AgrButton } from '@agrial/ui'
import AgrialCheckbox from '@/components/form/agrial-checkbox.vue'
import AgrialInput from '@/components/form/agrial-input.vue'
import AgrialInputNumber from '@/components/form/agrial-input-number.vue'
import AgrialSelect from '@/components/form/agrial-select.vue'
import BaseMessage from '@/components/common/base-message.vue'


export default {
  name: 'ContratAdherentForm',
  components: { AgrButton, AgrialCheckbox, AgrialInput, AgrialInputNumber, AgrialSelect, BaseMessage},
  props: {
    contrat: {
      type: Object,
      default: null
    },
    isFromListeContratsGlobal: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      rules: {
        required: value => (value !== null && value !== '') || 'Champ requis'
      },
      isEmptyString: isEmptyString,
      loading: false,
      erreur: null,
      contratModifiable: {},
      libelle: null,
      typeContrat: null,
      poidsEnTonne: null,
      typePrix: null,
      prixPayeEnEuroParTonne: null,
      isPrixProvisoire: false,
    }
  },

  computed: {
    ...mapGetters({
      typesContrat: 'contratVente/typesContrat',
      typesPrix: 'contratVente/typesPrix'
    }),
    isImportation () {
      return this.contrat.typeSaisie === 'IMPORTATION'
    },
    isSaisieManuelle () {
      return this.contrat.typeSaisie === 'SAISIE_MANUELLE'
    },
    isValid () {
      return this.libelle != null
      && !this.isEmptyString(this.libelle)
        && this.typeContrat != null
        && this.poidsEnTonne != null
        && this.typePrix != null
        && (((this.typePrix.code === 'PRIX_PAYE' || this.typePrix.code === 'PRIX_CONTRAT') && this.prixPayeEnEuroParTonne != null) || (this.typePrix.code === 'AUCUN' && this.prixPayeEnEuroParTonne == null))
    },
    isValidRepartition () {
      return this.contratModifiable!= null && this.contratModifiable.cultures!= null ? this.contratModifiable.cultures.filter(c => c.poidsEnTonne === null).length === 0 && !this.hasErreurCalculRepartition : true
    },
    hasErreurCalculRepartition () {
      return this.contratModifiable!= null && this.contratModifiable.cultures!= null && this.contratModifiable.cultures.length ? !equalDecimals(sum(this.contratModifiable.cultures.map(c => c.poidsEnTonne)),this.contrat.poidsEnTonne) : false
    }
  },

  watch: {
    contrat: {
      handler () {
        this.contratModifiable = clone(this.contrat)
        this.libelle = this.contrat != null ? this.contrat.libelle : null
        this.typeContrat = this.contrat != null ? this.contrat.typeContrat : null 
        this.poidsEnTonne = this.contrat != null ? this.contrat.poidsEnTonne : null 
        this.typePrix = this.contrat != null ? this.contrat.typePrix : null 
        this.prixPayeEnEuroParTonne = this.contrat != null ? this.contrat.prixPayeEnEuroParTonne : null 
        this.libelle = this.contrat != null ? this.contrat.libelle : null 
        this.isPrixProvisoire = this.contrat != null ? this.contrat.isPrixProvisoire : null
      },
      immediate: true
    }
  },
  created () {
    this.getTypesContrat()
    this.getTypesPrix()
  },
  methods: {
    ...mapActions({
      createOrUpdateContratVente: 'contratVente/createOrUpdateContratVente',
      getTypesContrat: 'contratVente/getTypesContrat',
      getTypesPrix: 'contratVente/getTypesPrix'
    }),
    saveContrat () {
      this.loading = true
      this.erreur = null
      this.contratModifiable.id = this.contrat.id
      this.contratModifiable.numtiers = this.contrat.numtiers
      this.contratModifiable.anneeRecolte = this.contrat.anneeRecolte
      
      if (!this.contrat.contratAgrial) {
        let culture = this.contrat.cultures[0]
        culture.poidsEnTonne = this.poidsEnTonne
        this.contratModifiable.contratAgrial = false
        this.contratModifiable.cultures = [culture]
      } else {
        this.contratModifiable.contratAgrial = true
      }
      this.contratModifiable.dateCreation = this.contrat.dateCreation
      this.contratModifiable.userCreation = this.contrat.userCreation
      this.contratModifiable.libelle = this.libelle
      this.contratModifiable.typeContrat = this.typeContrat 
      this.contratModifiable.poidsEnTonne = this.poidsEnTonne
      this.contratModifiable.typePrix = this.prixPayeEnEuroParTonne!= null && !this.isFromListeContratsGlobal ? this.typesPrix.find(t =>t.code ==='PRIX_PAYE') : this.typePrix
      this.contratModifiable.prixPayeEnEuroParTonne = this.prixPayeEnEuroParTonne 
      this.contratModifiable.isPrixProvisoire = this.isPrixProvisoire != null ? this.isPrixProvisoire: false
      this.createOrUpdateContratVente(this.contratModifiable)
        .then(() => {
          this.$emit('closeForm')
          this.$emit('reload')
        })
        .catch(err => {
          this.erreur = err.response.data.message
        })
        .finally(() => {
          this.loading = false
          this.libelle = null
          this.typeContrat = null 
          this.poidsEnTonne = null 
          this.typePrix = null 
          this.prixPayeEnEuroParTonne = null 
          this.libelle = null 
          this.isPrixProvisoire = false
        })
    },
    onClose () {
      this.$emit('closeForm')
    },
    updateTypePrix () {
      if (this.typePrix.code === 'AUCUN') {
        this.prixPayeEnEuroParTonne = null
      }
    }
  }
}
</script>
